import { useRef, useEffect } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getRoot, $createParagraphNode, $createTextNode, KEY_ENTER_COMMAND, COMMAND_PRIORITY_LOW } from "lexical";

const Boundary = ({ children }) => children;

function InitPlugin({ value }) {
  const [editor] = useLexicalComposerContext();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !value) return;
    done.current = true;
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      const p = $createParagraphNode();
      p.append($createTextNode(value));
      root.append(p);
    });
  }, []);
  return null;
}

// Optional — only registered when a caller passes onEnter (HoverTextField.jsx
// does, so Enter exits editing there the same as every plain <input> field;
// the other callers of this component don't pass it, so they keep Lexical's
// default newline-on-Enter behavior unchanged).
function EnterCommitPlugin({ onEnter }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    if (!onEnter) return undefined;
    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        event?.preventDefault();
        onEnter();
        return true;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, onEnter]);
  return null;
}

export default function PlainTextExpandingInput({ value, onChange, placeholder, namespace, onEnter }) {
  const initialConfig = {
    namespace: namespace || "plain-text-expanding",
    onError: console.error,
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div style={{ position: "relative" }} className="pts-plain-text-input">
        <PlainTextPlugin
          contentEditable={
            <ContentEditable
              className="form-control form-control-sm"
              style={{ minHeight: 31, wordBreak: "break-word", whiteSpace: "pre-wrap", padding: "3px 5px 4px", fontWeight: 400, color: "#495057" }}
            />
          }
          placeholder={
            <div style={{
              position: "absolute", top: 3, left: 5,
              color: "#adb5bd", pointerEvents: "none", fontSize: "0.875rem",
            }}>
              {placeholder}
            </div>
          }
          ErrorBoundary={Boundary}
        />
      </div>
      <OnChangePlugin
        onChange={state => state.read(() => onChange?.($getRoot().getTextContent()))}
      />
      <InitPlugin value={value} />
      <EnterCommitPlugin onEnter={onEnter} />
    </LexicalComposer>
  );
}
